export const americanHumanoidRobotsPage = {
  path: "/humanoid-robots/united-states",
  title: "American Humanoid Robots The United States Humanoid Robotics Ecosystem",
  seoTitle: "American Humanoid Robots Tesla Optimus, Figure 02, Digit, Apollo, Atlas, Valkyrie and More",
  seoDescription:
    "Explore the United States humanoid robotics ecosystem, from Tesla Optimus, Figure 02, Agility Digit and Apptronik Apollo to Boston Dynamics Atlas, NASA Valkyrie, Robonaut 2, IHMC Nadia and DARPA-era humanoid robots.",
  description:
    "The United States has one of the world’s strongest humanoid robotics ecosystems, combining AI labs, venture-backed startups, NASA platforms, DARPA-era research, warehouse pilots and industrial humanoid development.",
  updatedAt: "July 8, 2026",
  author: "TechniaHQRobot Robotics Desk",
  readingTime: "17 min read",
  keywords: [
    "American humanoid robots",
    "humanoid robots in the United States",
    "USA humanoid robot companies",
    "Tesla Optimus",
    "Figure 02",
    "Figure AI robot",
    "Agility Robotics Digit",
    "Apptronik Apollo",
    "Boston Dynamics Atlas",
    "NASA Valkyrie",
    "NASA Robonaut",
    "IHMC Nadia",
    "humanoid robot startups USA",
    "embodied AI USA",
    "physical AI USA",
    "warehouse humanoid robots",
    "industrial humanoid robots America",
    "DARPA humanoid robots"
  ],
  summaryCards: [
    {
      label: "Industrial humanoids",
      value: "5",
      detail: "Figure 03, Digit, Apollo, Atlas and Optimus are five industrial or general-purpose programs covered here. Their deployment stages differ."
    },
    {
      label: "Dynamic humanoids",
      value: "3",
      detail: "Atlas electric, Atlas hydraulic and PETMAN show the Boston Dynamics line from protective-suit testing to enterprise humanoid work."
    },
    {
      label: "Space and disaster research",
      value: "2",
      detail: "NASA Valkyrie / R5 and Robonaut 2 connect humanoid robotics to space operations, remote work and disaster-response research."
    },
    {
      label: "DARPA and university roots",
      value: "4+",
      detail: "THOR, THOR-OP, CHARLI, Nadia and Atlas share deep links to university labs, IHMC, NASA and DARPA-era field robotics."
    },
    {
      label: "Logistics and warehouse pilots",
      value: "4",
      detail: "Digit, Apollo, Figure and Optimus are watched most closely for tote handling, case picking, machine tending and factory tasks."
    },
    {
      label: "USA-linked watchlist",
      value: "6",
      detail: "1X NEO, Sanctuary Phoenix, K-Scale, Persona AI, Weave Isaac and Diligent Moxi need clear labels because not all are US-origin bipedal humanoids."
    }
  ],
  robots: [
    {
      id: "tesla-optimus",
      name: "Tesla Optimus",
      company: "Tesla",
      city: "Palo Alto, California / Austin, Texas",
      state: "California / Texas",
      status: "Active development",
      statusTone: "active",
      mainUse: "Repetitive work in human-designed environments, especially future factory and logistics tasks.",
      mobility: "Bipedal humanoid",
      yearIntroduced: "2021 announcement, 2022 prototype",
      keyHardware: "Tesla describes balance, navigation, perception and interaction software stacks. Detailed production specifications should be treated as evolving unless confirmed in Tesla material.",
      autonomy: "Development program. Public demonstrations, internal tests and future production claims should be separated from verified deployment.",
      overview:
        "Optimus is Tesla’s humanoid robot program for unsafe, repetitive or boring work. It belongs in the American ecosystem because Tesla has manufacturing scale, AI inference hardware, vision software and factory environments where a humanoid could be tested.",
      technical:
        "Tesla’s official AI page frames Optimus around balance, navigation, perception and interaction with the physical world. The page should not invent payload, battery life, hand degrees of freedom or cost unless those numbers come from Tesla source material.",
      whyItMatters:
        "Optimus matters because Tesla can connect robot hardware with vehicle-scale perception engineering, in-house manufacturing and a large real-world operations network. The open question is whether that stack becomes reliable enough for repeatable physical work.",
      limitations:
        "Treat Optimus as an active development platform. Do not present it as a proven household robot or broad commercial worker. Public demos may involve scripted behaviors, teleoperation, resets or limited task scope.",
      sourceLabel: "Tesla AI and Robotics",
      sourceUrl: "https://www.tesla.com/AI"
    },
    {
      id: "figure-01",
      name: "Figure 01",
      company: "Figure AI",
      city: "Sunnyvale / San Jose, California",
      state: "California",
      status: "Early platform",
      statusTone: "prototype",
      mainUse: "Industrial humanoid development, manipulation research and early customer evaluation.",
      mobility: "Bipedal humanoid",
      yearIntroduced: "2023 public reveal period",
      keyHardware: "Public information should be kept high level unless Figure confirms a specification. Present it as the predecessor to Figure 02.",
      autonomy: "Early development and demonstrations, not a broad deployment platform.",
      overview:
        "Figure 01 is the first visible platform from Figure AI. It set the design direction for an American startup focused on humanoids for manufacturing, logistics and warehousing.",
      technical:
        "Use Figure 01 to explain iteration. It showed the company’s first full-body approach before Figure 02 added a cleaner industrial design, more compute and a redesigned hand system.",
      whyItMatters:
        "Figure 01 matters because it turned Figure from a funded startup into a visible hardware company. It also created the baseline for later BMW factory tests and Figure’s newer product line.",
      limitations:
        "Do not overstate Figure 01. The stronger current evidence sits around Figure 02, Figure 03 and Helix. Figure 01 belongs in the history section and comparison table, not as the latest platform.",
      sourceLabel: "Figure AI",
      sourceUrl: "https://www.figure.ai/"
    },
    {
      id: "figure-02",
      name: "Figure 02",
      company: "Figure AI",
      city: "Sunnyvale / San Jose, California",
      state: "California",
      status: "Retired industrial platform",
      statusTone: "historical",
      mainUse: "Historical sheet-metal loading at BMW Plant Spartanburg.",
      mobility: "Bipedal humanoid",
      yearIntroduced: "2024",
      keyHardware: "Reported Figure 02 details include integrated limb cabling, torso battery, six RGB cameras, onboard AI compute and five-finger hands with 16 degrees of freedom.",
      autonomy: "Figure reports an 11-month BMW production deployment; task-specific evidence does not establish general autonomy.",
      overview:
        "Figure began retiring Figure 02 in November 2025 after its BMW production deployment. It is retained here as a historical industrial platform.",
      technical:
        "Figure 02 is commonly described with six RGB cameras, onboard compute, integrated wiring, a torso battery and five-finger hands. The page should present those details as model-specific and source-dependent.",
      whyItMatters:
        "Figure 02 matters because it moved Figure into factory discussion with BMW and gave the US market a polished industrial humanoid competitor next to Tesla, Apptronik, Agility and Boston Dynamics.",
      limitations:
        "The BMW results concern sheet-metal loading. They do not establish performance on other tasks or current product availability.",
      sourceLabel: "Figure BMW deployment and retirement report",
      sourceUrl: "https://www.figure.ai/news/production-at-bmw"
    },
    {
      id: "figure-03",
      name: "Figure 03",
      company: "Figure AI",
      city: "Sunnyvale / San Jose, California",
      state: "California",
      status: "Latest Figure platform",
      statusTone: "active",
      mainUse: "General-purpose humanoid work with a stronger focus on home and everyday tasks in public positioning.",
      mobility: "Bipedal humanoid",
      yearIntroduced: "2025",
      keyHardware: "Figure’s current site presents Figure 03 with Helix intelligence for changing home environments. Detailed hardware claims should use Figure’s own product pages.",
      autonomy: "Figure says Helix supports navigation in changing environments. Treat home capability as early and environment-dependent.",
      overview:
        "Figure 03 is included because the live Figure site now positions it as the current general-purpose humanoid robot. It should sit above Figure 02 in recency while Figure 02 remains important for factory context.",
      technical:
        "The key technical story is the coupling of Figure hardware with Helix. The page should avoid claiming household autonomy across all chores until independent deployment evidence exists.",
      whyItMatters:
        "Figure 03 shows how American humanoid startups are moving from factory pilots toward home-facing narratives. That shift increases the need for careful language about teleoperation, privacy and task limits.",
      limitations:
        "Do not call Figure 03 a solved home robot. Real homes include clutter, pets, fragile objects, stairs, private data and unpredictable human behavior.",
      sourceLabel: "Figure 03",
      sourceUrl: "https://www.figure.ai/"
    },
    {
      id: "digit",
      name: "Digit",
      company: "Agility Robotics / Agility",
      city: "Salem, Oregon",
      state: "Oregon",
      status: "Commercial logistics humanoid",
      statusTone: "active",
      mainUse: "Warehouse and logistics work such as tote movement, material handling and case-flow support.",
      mobility: "Bipedal humanoid with arms and non-humanlike leg geometry",
      yearIntroduced: "2017 research line, 2023 industrial redesign",
      keyHardware: "Legged body, arms, perception, expressive head display and Agility Arc software around workcell deployment.",
      autonomy: "Industrial deployment in constrained workflows. Human-safe collaboration and broader autonomy remain staged by certification and environment.",
      overview:
        "Digit is one of the clearest American attempts to move humanoid robots from lab demonstrations into warehouse labor. Its body is not a human copy. The legs, torso and arms are optimized for moving totes in spaces built for people.",
      technical:
        "Agility describes a progression from Oregon State research to Cassie and then Digit. The current public company page lists Salem, Oregon, RoboFab and industrial customers while emphasizing real deployment over general claims.",
      whyItMatters:
        "Digit matters because it is tied to logistics customers, a dedicated Oregon factory and a narrow material-handling job. This makes it more measurable than a broad home-assistant promise.",
      limitations:
        "Digit’s strongest evidence sits in structured warehouse workflows. Do not present it as a general household humanoid or a robot that can replace every warehouse task.",
      sourceLabel: "Agility company page",
      sourceUrl: "https://www.agilityrobotics.com/company"
    },
    {
      id: "cassie",
      name: "Cassie",
      company: "Agility Robotics / Oregon State University roots",
      city: "Corvallis / Salem, Oregon",
      state: "Oregon",
      status: "Bipedal research platform",
      statusTone: "research",
      mainUse: "Dynamic bipedal locomotion research, not full humanoid warehouse work.",
      mobility: "Bipedal legs without a full humanoid upper body",
      yearIntroduced: "2016",
      keyHardware: "Two-legged platform focused on balance, walking and running research. No full humanoid torso or manipulation package.",
      autonomy: "Research locomotion platform.",
      overview:
        "Cassie should be included with a clear label. It is not a full humanoid worker. It is the bipedal foundation that helped Agility move toward Digit.",
      technical:
        "Cassie’s value is locomotion. The platform removes human-like upper-body complexity so researchers can focus on dynamic walking, control and balance.",
      whyItMatters:
        "Cassie matters because the US humanoid ecosystem depends on legged locomotion research, not only hands and AI models. It is a bridge from university lab work to industrial humanoid design.",
      limitations:
        "Do not classify Cassie as a full humanoid robot. It belongs in research and history sections, not in the active industrial humanoid table without a warning.",
      sourceLabel: "Agility company page",
      sourceUrl: "https://www.agilityrobotics.com/company"
    },
    {
      id: "apollo",
      name: "Apollo / Apollo 2",
      company: "Apptronik",
      city: "Austin, Texas",
      state: "Texas",
      status: "Active general-purpose humanoid platform",
      statusTone: "active",
      mainUse: "Warehouse, manufacturing, trailer unloading, case picking, palletization, machine tending and workcell delivery.",
      mobility: "Modular humanoid, stationary or mobile with legs depending on configuration",
      yearIntroduced: "2023 Apollo, 2026 Apollo 2 news cycle",
      keyHardware: "Official Apollo specs list 5 ft 8 in height, 160 lb weight, 55 lb payload and four-hour battery packs.",
      autonomy: "Customer pilots and training environments. Apptronik emphasizes software integration and point-and-click fleet control.",
      overview:
        "Apollo is Apptronik’s Austin-built humanoid platform for logistics and industrial work. It comes from a company with experience building more than ten previous robots, including work connected to NASA Valkyrie.",
      technical:
        "Apptronik lists Apollo as 5 ft 8 in tall, 160 lb, with a 55 lb payload and four hours per battery pack. The design is modular and can be stationary or mobile with legs.",
      whyItMatters:
        "Apollo matters because it ties Texas humanoid robotics to NASA-linked engineering, Google DeepMind collaboration, Mercedes-Benz interest and industrial data collection.",
      limitations:
        "Apollo should not be written as deployed at broad commercial scale. It belongs in pilots, training environments and customer evaluation unless a source confirms production deployment.",
      sourceLabel: "Apptronik Apollo",
      sourceUrl: "https://apptronik.com/our-work/apollo-1"
    },
    {
      id: "atlas-electric",
      name: "Atlas electric",
      company: "Boston Dynamics",
      city: "Waltham, Massachusetts",
      state: "Massachusetts",
      status: "Active enterprise humanoid platform",
      statusTone: "active",
      mainUse: "Industrial material handling, part sequencing, machine tending and enterprise automation trials.",
      mobility: "Bipedal humanoid with high range of motion",
      yearIntroduced: "2024",
      keyHardware: "Boston Dynamics lists 56 DoF, 1.9 m height, 90 kg weight, 4-hour battery life, 50 kg instant capacity, 30 kg sustained capacity, tactile sensing and 360-degree camera view.",
      autonomy: "Boston Dynamics describes autonomous work with minimal supervision for material handling. State task scope clearly.",
      overview:
        "The electric Atlas is the post-hydraulic generation of Boston Dynamics’ humanoid work. It should be separated from the older Atlas because the platform, commercial aim and hardware package changed.",
      technical:
        "Boston Dynamics now presents Atlas as an enterprise humanoid with high-power actuation, 56 DoF, tactile sensing, 360-degree camera view and self-swappable batteries. These specs should be attached directly to the official Atlas page.",
      whyItMatters:
        "Atlas matters because Boston Dynamics has spent more than a decade testing dynamic whole-body movement. The electric platform moves that research closer to industrial work instead of viral motion demos alone.",
      limitations:
        "Even strong autonomous task videos do not prove broad deployment. Keep the wording tied to part handling, material handling and named pilots.",
      sourceLabel: "Boston Dynamics Atlas",
      sourceUrl: "https://bostondynamics.com/products/atlas/"
    },
    {
      id: "atlas-hydraulic",
      name: "Atlas hydraulic",
      company: "Boston Dynamics / DARPA",
      city: "Waltham, Massachusetts",
      state: "Massachusetts",
      status: "Historical hydraulic platform",
      statusTone: "historical",
      mainUse: "DARPA-era locomotion, rescue robotics research and dynamic motion demonstrations.",
      mobility: "Hydraulic bipedal humanoid",
      yearIntroduced: "2013",
      keyHardware: "Hydraulic actuation, full-body dynamic locomotion and research hardware developed during the DARPA Robotics Challenge era.",
      autonomy: "Historical research and demonstrations.",
      overview:
        "The hydraulic Atlas made Boston Dynamics a global reference for humanoid movement. It ran, jumped, flipped and fell in videos that made dynamic humanoid control visible to the public.",
      technical:
        "This older platform should be framed as hydraulic research hardware, not the current enterprise product. It belongs in the history and DARPA sections.",
      whyItMatters:
        "Hydraulic Atlas matters because many current humanoid expectations trace back to Boston Dynamics proving that a human-sized robot could perform aggressive whole-body motion.",
      limitations:
        "The platform is historical. Do not use old parkour videos as proof that the new electric Atlas is commercially deployed everywhere.",
      sourceLabel: "Boston Dynamics Atlas",
      sourceUrl: "https://bostondynamics.com/products/atlas/"
    },
    {
      id: "petman",
      name: "PETMAN",
      company: "Boston Dynamics",
      city: "Waltham, Massachusetts",
      state: "Massachusetts",
      status: "Historical humanoid test platform",
      statusTone: "historical",
      mainUse: "Protective clothing and chemical suit testing.",
      mobility: "Bipedal humanoid mannequin-like robot",
      yearIntroduced: "2009 to 2011 public period",
      keyHardware: "Anthropomorphic biped designed to move dynamically like a person for testing protective ensembles.",
      autonomy: "Historical test platform.",
      overview:
        "PETMAN belongs in the page because it is an American humanoid ancestor to Atlas. Its job was not warehouse work. It tested protective clothing under human-like motion.",
      technical:
        "The technical value is repeatable human-like movement in controlled test conditions, especially for protective suit evaluation.",
      whyItMatters:
        "PETMAN shows that US humanoid robotics was not only about social robots or AI. Some early platforms were built for military, safety and human-equipment testing.",
      limitations:
        "PETMAN is historical. Do not classify it as an active humanoid company product.",
      sourceLabel: "Boston Dynamics history",
      sourceUrl: "https://bostondynamics.com/company/"
    },
    {
      id: "valkyrie-r5",
      name: "Valkyrie / R5",
      company: "NASA Johnson Space Center",
      city: "Houston, Texas",
      state: "Texas",
      status: "Research humanoid platform",
      statusTone: "research",
      mainUse: "Disaster-response research, space robotics and supervised operation in extreme environments.",
      mobility: "Bipedal humanoid research robot",
      yearIntroduced: "2013",
      keyHardware: "NASA R5 is widely described as a 6-foot-class humanoid with whole-body mobility, hands and onboard computing for research.",
      autonomy: "Research and supervised autonomy. It is not a commercial worker.",
      overview:
        "Valkyrie, also called R5, was built by NASA Johnson Space Center for disaster-response and space-related robotics research. It connects humanoid robotics to remote work where humans may be delayed, distant or at risk.",
      technical:
        "Valkyrie profiles should focus on whole-body mobility, manipulation, remote operation and research use. Avoid treating it as a modern commercial product.",
      whyItMatters:
        "Valkyrie matters because NASA gave the US humanoid field a large research platform linked to Mars precursor work, disaster response and remote operation.",
      limitations:
        "Valkyrie is slow and research-oriented compared with current warehouse humanoids. It should be presented as a research platform with lessons for autonomy and operator interfaces.",
      sourceLabel: "NASA R5 / Valkyrie",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      id: "robonaut-2",
      name: "Robonaut 2",
      company: "NASA / General Motors",
      city: "Houston, Texas / United States",
      state: "Texas / USA",
      status: "Historical space humanoid research",
      statusTone: "historical",
      mainUse: "Space robotics research, astronaut assistance and dexterous tool handling.",
      mobility: "Upper-body humanoid robot, originally torso-based for ISS work",
      yearIntroduced: "2010 public reveal, 2011 ISS delivery",
      keyHardware: "Upper body, dexterous arms, hands and sensors designed around using tools made for astronauts.",
      autonomy: "Telepresence and partial autonomy in research contexts.",
      overview:
        "Robonaut 2 is one of the most important American humanoid robots because it reached the International Space Station. It was built around dexterity and astronaut-adjacent work rather than walking.",
      technical:
        "The key hardware story is the upper body, hands and ability to use human tools. It should be classified separately from bipedal humanoids.",
      whyItMatters:
        "Robonaut 2 matters because it proves that humanoid form can make sense when the environment is already designed for human hands, tools and procedures.",
      limitations:
        "Robonaut 2 is not a modern factory humanoid and should not be compared directly with Digit or Apollo on locomotion.",
      sourceLabel: "NASA Robonaut",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      id: "nadia",
      name: "Nadia",
      company: "IHMC / Boardwalk Robotics",
      city: "Pensacola, Florida",
      state: "Florida",
      status: "Research humanoid platform",
      statusTone: "research",
      mainUse: "Loco-manipulation, supervised operation and whole-body humanoid research.",
      mobility: "Bipedal humanoid research platform",
      yearIntroduced: "2020s research platform",
      keyHardware: "Public material links Nadia to IHMC humanoid research and Boardwalk Robotics hardware. Use precise specifications only when confirmed by project material.",
      autonomy: "Research, supervised behavior and operator-directed tasks.",
      overview:
        "Nadia belongs in the US ecosystem because it connects IHMC’s long DRC-era humanoid control work with a modern research machine for whole-body tasks.",
      technical:
        "The page should describe Nadia through locomotion, manipulation, operator interfaces and behavior authoring instead of commercial deployment language.",
      whyItMatters:
        "Nadia matters because many humanoid failures happen at the boundary between walking, reaching, contact and recovery. IHMC’s work is centered on that boundary.",
      limitations:
        "Nadia is a research robot. Do not list it as a consumer or factory product unless future sources confirm a commercial package.",
      sourceLabel: "IHMC Robotics",
      sourceUrl: "https://robots.ihmc.us/"
    },
    {
      id: "thor-thor-op",
      name: "THOR / THOR-OP",
      company: "Virginia Tech",
      city: "Blacksburg, Virginia",
      state: "Virginia",
      status: "DARPA-era academic platform",
      statusTone: "darpa",
      mainUse: "Disaster-response research, DARPA Robotics Challenge work and academic humanoid development.",
      mobility: "Bipedal humanoid research platform",
      yearIntroduced: "2010s DARPA Robotics Challenge period",
      keyHardware: "Academic humanoid platform developed around whole-body walking, manipulation and challenge tasks.",
      autonomy: "Research and competition autonomy, often with operator involvement.",
      overview:
        "THOR and THOR-OP represent the Virginia Tech side of the US humanoid research story. They belong with DARPA-era platforms, not modern commercial warehouse robots.",
      technical:
        "The robot profile should focus on whole-body disaster-response tasks, walking, manipulation and academic control research.",
      whyItMatters:
        "THOR matters because the DARPA Robotics Challenge forced teams to test humanoids on doors, valves, tools, rough terrain and degraded communications.",
      limitations:
        "Do not present THOR as an active commercial product. It is a research and competition-era platform.",
      sourceLabel: "Virginia Tech Robotics",
      sourceUrl: "https://www.vt.edu/"
    },
    {
      id: "charli",
      name: "CHARLI / CHARLI-2",
      company: "Virginia Tech RoMeLa",
      city: "Blacksburg, Virginia",
      state: "Virginia",
      status: "Academic humanoid platform",
      statusTone: "research",
      mainUse: "Bipedal walking, RoboCup, shipboard robotics research and humanoid education.",
      mobility: "Bipedal humanoid research platform",
      yearIntroduced: "2009 to 2012 public period",
      keyHardware: "Human-scale academic humanoid with walking and balance research. Detailed specs should use university or paper sources.",
      autonomy: "Academic research and demonstrations.",
      overview:
        "CHARLI and CHARLI-2 show how American universities trained the generation that later built stronger humanoid systems. The platform is part of the academic layer of the ecosystem.",
      technical:
        "Keep the technical description focused on walking, balance, RoboCup and shipboard firefighting research connections.",
      whyItMatters:
        "CHARLI matters because US humanoid robotics did not start with current startups. It came through university labs, competitions, Navy-funded research and years of bipedal control work.",
      limitations:
        "CHARLI is historical academic robotics, not a current commercial platform.",
      sourceLabel: "Virginia Tech RoMeLa",
      sourceUrl: "https://www.romela.org/"
    }
  ],
  watchlist: [
    {
      name: "1X NEO",
      origin: "Norway-origin, USA-linked",
      note: "1X has a strong US market presence and Palo Alto links, but NEO should not be classified as purely American-origin. Mention teleoperation and home-environment limits when discussing early consumer units.",
      sourceUrl: "https://www.1x.tech/",
      internalLink: {
        label: "Read the 1X NEO hand analysis",
        to: "/robotics-news/1x-neo-25-dof-tendon-driven-hands"
      }
    },
    {
      name: "Sanctuary Phoenix",
      origin: "Canada-origin, North America watchlist",
      note: "Phoenix belongs in a North America note, not in the core US robot table. Sanctuary AI is Canadian.",
      sourceUrl: "https://www.sanctuary.ai/"
    },
    {
      name: "K-Scale K-Bot",
      origin: "USA watchlist",
      note: "Include only when official public material confirms current product status, origin and specifications.",
      sourceUrl: "https://kscale.dev/"
    },
    {
      name: "Persona AI",
      origin: "USA watchlist",
      note: "Houston-based humanoid startup focused on heavy industry. Keep it as watchlist until detailed public robot specifications and deployments are confirmed.",
      sourceUrl: "https://personainc.ai/"
    },
    {
      name: "Weave Robotics Isaac",
      origin: "USA watchlist, wheeled home robot",
      note: "Isaac should be described carefully as a wheeled home robot with humanoid-like manipulation, not a classic bipedal humanoid worker.",
      sourceUrl: "https://www.weaverobotics.com/"
    },
    {
      name: "Diligent Robotics Moxi",
      origin: "USA service robot, not bipedal humanoid",
      note: "Moxi is useful for hospital service robotics context, but it should not sit in the main humanoid table unless clearly labeled as a mobile service robot.",
      sourceUrl: "https://www.diligentrobots.com/"
    }
  ],
  mapLocations: [
    {
      place: "California",
      robots: "Tesla Optimus, Figure AI, AI labs, robotics startups",
      detail: "The state concentrates AI talent, venture capital, simulation teams and humanoid startup hiring."
    },
    {
      place: "Oregon",
      robots: "Agility Robotics, Digit, Cassie and Oregon State roots",
      detail: "Salem and Corvallis connect university locomotion research to RoboFab and warehouse humanoid production."
    },
    {
      place: "Texas",
      robots: "Apptronik Apollo, NASA Johnson Space Center, Tesla operations",
      detail: "Austin and Houston link industrial humanoids with NASA humanoid research and manufacturing operations."
    },
    {
      place: "Massachusetts",
      robots: "Boston Dynamics Atlas and PETMAN",
      detail: "Waltham remains one of the strongest global centers for dynamic legged robotics."
    },
    {
      place: "Florida",
      robots: "IHMC Nadia research",
      detail: "Pensacola anchors IHMC’s long work on humanoid control, operator interfaces and loco-manipulation."
    },
    {
      place: "Virginia",
      robots: "Virginia Tech THOR, THOR-OP and CHARLI",
      detail: "Virginia Tech adds the academic and DARPA Robotics Challenge layer."
    },
    {
      place: "Pittsburgh / Pennsylvania",
      robots: "Robotics engineering, autonomy testing and validation ecosystem",
      detail: "Use as ecosystem context. Only attach specific humanoid claims when a source confirms them."
    },
    {
      place: "Georgia",
      robots: "Warehouse and automotive pilot locations",
      detail: "Mention only when a named company confirms a customer site or pilot environment."
    }
  ],
  timeline: [
    {
      year: "1960s to 1990s",
      title: "US robotics research and space robotics foundations grow",
      detail: "NASA, universities and defense-funded labs build the base for teleoperation, manipulation and robots in dangerous environments.",
      sourceLabel: "NASA robotics",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      year: "2000s",
      title: "Robonaut connects humanoid form to space work",
      detail: "NASA’s Robonaut program focuses on dexterous humanoid upper-body work around human tools and astronaut procedures.",
      sourceLabel: "NASA Robonaut",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      year: "2011",
      title: "Robonaut 2 reaches the International Space Station",
      detail: "R2 becomes a key reference for humanoid work in space robotics, telepresence and partial autonomy.",
      sourceLabel: "NASA Robonaut",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      year: "2013",
      title: "Boston Dynamics Atlas enters the DARPA Robotics Challenge era",
      detail: "The hydraulic Atlas platform becomes a public symbol of dynamic humanoid movement and disaster-response research.",
      sourceLabel: "Boston Dynamics Atlas",
      sourceUrl: "https://bostondynamics.com/products/atlas/"
    },
    {
      year: "2013 to 2015",
      title: "NASA Valkyrie / R5 becomes a humanoid research platform",
      detail: "Valkyrie connects NASA work to supervised autonomy, disaster response and space-related remote operations.",
      sourceLabel: "NASA R5",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      year: "2015",
      title: "Agility is founded from Oregon State robotics research",
      detail: "Agility’s public history links its origin to the Dynamic Robotics Lab and the path toward Cassie and Digit.",
      sourceLabel: "Agility company page",
      sourceUrl: "https://www.agilityrobotics.com/company"
    },
    {
      year: "2016 to 2017",
      title: "Cassie and Digit emerge from Agility’s bipedal work",
      detail: "Cassie focuses on locomotion. Digit adds a humanoid upper body and a clearer path toward logistics work.",
      sourceLabel: "Agility company page",
      sourceUrl: "https://www.agilityrobotics.com/company"
    },
    {
      year: "2021",
      title: "Tesla announces Optimus / Tesla Bot",
      detail: "Tesla presents a humanoid program aimed at tasks that are unsafe, repetitive or boring.",
      sourceLabel: "Tesla AI",
      sourceUrl: "https://www.tesla.com/AI"
    },
    {
      year: "2023",
      title: "Apollo, Figure and new US humanoid startups gain visibility",
      detail: "Apptronik Apollo and Figure AI push the US startup scene toward warehouse and industrial humanoid narratives.",
      sourceLabel: "Apptronik Apollo",
      sourceUrl: "https://apptronik.com/our-work/apollo-1"
    },
    {
      year: "2024",
      title: "Boston Dynamics reveals electric Atlas",
      detail: "The electric platform replaces the hydraulic research lineage with a clearer enterprise humanoid direction.",
      sourceLabel: "Boston Dynamics Atlas",
      sourceUrl: "https://bostondynamics.com/products/atlas/"
    },
    {
      year: "2024",
      title: "Figure 02 arrives as a newer industrial humanoid platform",
      detail: "Figure 02 strengthens the US competition around hands, onboard compute, perception and factory trials.",
      sourceLabel: "Figure AI",
      sourceUrl: "https://www.figure.ai/"
    },
    {
      year: "2025 to 2026",
      title: "US humanoid companies push pilots, data collection and production planning",
      detail: "The market shifts toward Robot Parks, RoboFab, field testing, customer pilots and narrower measurable workflows.",
      sourceLabel: "Company source review",
      sourceUrl: "https://www.agilityrobotics.com/company"
    }
  ],
  classification: [
    {
      title: "Active or recent American humanoid platforms",
      items: ["Tesla Optimus", "Figure 03", "Agility Digit", "Apptronik Apollo", "Boston Dynamics Atlas electric", "IHMC Nadia"]
    },
    {
      title: "Research and government platforms",
      items: ["NASA Valkyrie / R5", "NASA Robonaut 2", "THOR / THOR-OP", "CHARLI / CHARLI-2", "Cassie as a bipedal research platform"]
    },
    {
      title: "Historical platforms",
      items: ["Figure 01", "Figure 02", "Boston Dynamics Atlas hydraulic", "PETMAN", "Robonaut 1", "Early NASA and DARPA humanoid platforms", "Meka humanoid platforms if reliable sources confirm details"]
    },
    {
      title: "USA-linked but not purely American-origin",
      items: ["1X NEO, Norway-origin with US market and operations link", "Sanctuary Phoenix, Canada-origin", "HUBO DRC, Korean-origin DARPA competition participant", "Sophia, Hong Kong-origin and not American"]
    },
    {
      title: "Watchlist only",
      items: ["K-Scale K-Bot", "Persona AI humanoid", "Weave Robotics Isaac", "Stealth humanoid startups", "Any robot seen only in a viral demo without a verified product page"]
    }
  ],
  whyUnitedStatesMatters: [
    {
      title: "AI labs and robot learning",
      detail: "The US has frontier AI companies, robotics foundation model work, simulation teams and a dense hiring market for perception, planning and policy learning."
    },
    {
      title: "DARPA, NASA and universities",
      detail: "Robonaut, Valkyrie, Atlas, THOR, CHARLI and Nadia show how government and university research created a long technical base before the current startup wave."
    },
    {
      title: "Warehouse and manufacturing demand",
      detail: "Digit, Apollo and Figure are aimed at work where humans already move boxes, parts, totes and tools through spaces designed around human bodies."
    },
    {
      title: "Venture capital and industrial customers",
      detail: "Startups can raise large rounds, hire hardware teams and test with automotive, logistics, retail and industrial partners. The money matters, but only if it converts into uptime, safety and cost per task."
    },
    {
      title: "Software, simulation and compute stack",
      detail: "Humanoid deployment needs more than motors. It needs data pipelines, fleet software, teleoperation, safety cases, simulation and integration with warehouse or factory systems."
    }
  ],
  limitations: [
    "Most humanoids are still in demos, pilots, training facilities or supervised environments.",
    "Factory and warehouse pilots do not automatically mean large-scale deployment.",
    "Dexterous manipulation, reliability, battery life, safety certification and maintenance remain difficult.",
    "Teleoperation and scripted demos are not the same as general autonomy.",
    "Public videos often hide resets, operator help, narrow task scope and failed attempts.",
    "Industrial ROI depends on uptime, integration, safety, repeatability, service cost and cost per task.",
    "A robot that can walk, wave or fold one shirt once is not automatically useful in daily operations.",
    "Warehouses and factories need predictable performance across thousands of cycles, not one clean clip."
  ],
  companyCards: [
    {
      name: "Tesla",
      founded: "2003",
      headquarters: "Austin, Texas",
      robot: "Optimus",
      useCase: "General-purpose humanoid development for repetitive work",
      status: "Active development",
      sourceUrl: "https://www.tesla.com/AI"
    },
    {
      name: "Figure AI",
      founded: "2022",
      headquarters: "California",
      robot: "Figure 01, Figure 02, Figure 03",
      useCase: "Industrial and general-purpose humanoid robots",
      status: "Active startup",
      sourceUrl: "https://www.figure.ai/"
    },
    {
      name: "Agility Robotics / Agility",
      founded: "2015",
      headquarters: "Salem, Oregon",
      robot: "Digit",
      useCase: "Warehouse and logistics humanoid robotics",
      status: "Commercial logistics focus",
      sourceUrl: "https://www.agilityrobotics.com/company"
    },
    {
      name: "Apptronik",
      founded: "2016",
      headquarters: "Austin, Texas",
      robot: "Apollo / Apollo 2",
      useCase: "Warehouse, manufacturing and industrial tasks",
      status: "Active platform and pilots",
      sourceUrl: "https://apptronik.com/our-work/apollo-1"
    },
    {
      name: "Boston Dynamics",
      founded: "1992",
      headquarters: "Waltham, Massachusetts",
      robot: "Atlas",
      useCase: "Dynamic humanoid robotics and enterprise automation",
      status: "Active Atlas electric platform",
      sourceUrl: "https://bostondynamics.com/products/atlas/"
    },
    {
      name: "NASA Johnson Space Center",
      founded: "1961 center history",
      headquarters: "Houston, Texas",
      robot: "Valkyrie and Robonaut",
      useCase: "Space, research and remote operation humanoids",
      status: "Research and historical platforms",
      sourceUrl: "https://www.nasa.gov/"
    },
    {
      name: "IHMC",
      founded: "1990",
      headquarters: "Pensacola, Florida",
      robot: "Nadia",
      useCase: "Humanoid control, loco-manipulation and supervised operation",
      status: "Research",
      sourceUrl: "https://robots.ihmc.us/"
    },
    {
      name: "Virginia Tech RoMeLa",
      founded: "University lab",
      headquarters: "Blacksburg, Virginia",
      robot: "CHARLI, THOR and THOR-OP",
      useCase: "Academic humanoid robotics and DARPA-era research",
      status: "Research and historical",
      sourceUrl: "https://www.romela.org/"
    }
  ],
  globalComparison: [
    {
      region: "United States",
      strength: "AI labs, venture-backed startups, DARPA and NASA history, warehouse pilots and high-end robotics software.",
      caution: "The US still has to prove cost, uptime, maintenance, safety certification and repeatable deployment."
    },
    {
      region: "China",
      strength: "Fast hardware iteration, dense manufacturing supply chains, lower-cost platforms and aggressive embodied AI data collection.",
      caution: "Separate production volume, demo frequency and verified autonomous deployment. They are different metrics."
    },
    {
      region: "Japan",
      strength: "Long history in bipedal walking, androids, social robots and human-robot interaction.",
      caution: "Some famous platforms are historical or discontinued, so status labels matter."
    },
    {
      region: "Europe",
      strength: "Strong robotics research, safety culture, industrial engineering and specialized humanoid companies.",
      caution: "The ecosystem is distributed across countries and does not always match US or Chinese funding speed."
    }
  ],
  internalLinks: [
    { label: "Humanoid robots main page", to: "/robot-types/humanoid-robots" },
    { label: "French humanoid robots", to: "/humanoid-robots/france" },
    { label: "Japanese humanoid robots", to: "/humanoid-robots/japan" },
    { label: "Chinese humanoid robots", to: "/humanoid-robots/china" },
    { label: "Humanoid robot companies", to: "/robotics-companies" },
    { label: "Physical AI", to: "/robotics-data-collection" },
    { label: "Embodied AI", to: "/robotics-data-collection" },
    { label: "Robotics software", to: "/robotics-software" },
    { label: "Robot components", to: "/robot-components" },
    { label: "Warehouse robotics", to: "/robot-types/warehouse-robots-amrs" },
    { label: "Industrial robots", to: "/robot-types/industrial-robots" }
  ]
};
